import { Link } from "react-router-dom";
import { Cloud } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="flex items-center gap-2">
            <Cloud className="w-8 h-8 text-bronze" />
            <span className="text-xl font-bold text-midnight font-outfit">CloudSync Pro</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-midnight font-playfair mb-2">Privacy Policy</h1>
        <p className="text-slate-500 mb-8">Last updated: January 2025</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">1. Introduction</h2>
            <p className="text-slate-600 mb-4">
              CloudSync Pro ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our WordPress plugin and related services.
            </p>
            <p className="text-slate-600">
              By using CloudSync Pro, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">2.1 Personal Information</h3>
            <p className="text-slate-600 mb-4">When you purchase or use our plugin, we may collect:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Name and email address</li>
              <li>Company/hotel name</li>
              <li>Billing information (processed securely by Stripe)</li>
              <li>WooCommerce site URL</li>
              <li>Cloudbeds property information</li>
            </ul>

            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">2.2 Transaction Data</h3>
            <p className="text-slate-600 mb-4">Our plugin collects booking transaction data including:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Booking amounts and currencies</li>
              <li>Guest names and email addresses</li>
              <li>Room types and booking dates</li>
              <li>Payment processor used (Scotiabank or Visa)</li>
              <li>Order IDs and reservation IDs</li>
            </ul>

            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">2.3 Technical Data</h3>
            <p className="text-slate-600 mb-4">We automatically collect:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>IP addresses</li>
              <li>Browser type and version</li>
              <li>WordPress and WooCommerce versions</li>
              <li>Plugin usage statistics</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use the collected information to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Process your plugin purchase and deliver your license key</li>
              <li>Validate and manage your plugin license</li>
              <li>Synchronize data between Cloudbeds and WooCommerce</li>
              <li>Calculate and process the 2% transaction fee</li>
              <li>Provide customer support</li>
              <li>Send important updates about the plugin</li>
              <li>Improve our services and develop new features</li>
              <li>Prevent fraud and unauthorized use</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">4. Payment Processing</h2>
            <p className="text-slate-600 mb-4">
              All payments are processed by <strong>Sebastian Alliance Group</strong>. This is the name that will appear on your credit card or bank statement. We use Stripe as our payment processor, which maintains PCI-DSS compliance.
            </p>
            <p className="text-slate-600">
              We do not store your complete credit card information on our servers. Payment details are securely handled by Stripe.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-slate-600 mb-4">We may share your information with:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Cloudbeds:</strong> To synchronize reservations and availability</li>
              <li><strong>Payment Processors:</strong> Stripe, Scotiabank, Bank of the Bahamas, and Visa payment networks</li>
              <li><strong>Service Providers:</strong> Hosting, analytics, and support services</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
            <p className="text-slate-600 mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">6. Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement appropriate security measures including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>SSL/TLS encryption for all data transmission</li>
              <li>Secure API authentication</li>
              <li>Regular security audits</li>
              <li>Access controls and monitoring</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">7. Data Retention</h2>
            <p className="text-slate-600">
              We retain your personal information for as long as your account is active or as needed to provide services. Transaction data is retained for 7 years for financial record-keeping purposes. You may request deletion of your data by contacting us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">8. Your Rights</h2>
            <p className="text-slate-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to data processing</li>
              <li>Data portability</li>
              <li>Withdraw consent</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">9. Cookies</h2>
            <p className="text-slate-600">
              Our website uses cookies to enhance your experience, analyze site traffic, and for authentication purposes. You can control cookie preferences through your browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">10. Children's Privacy</h2>
            <p className="text-slate-600">
              Our services are not directed to individuals under 18. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">11. Changes to This Policy</h2>
            <p className="text-slate-600">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">12. Contact Us</h2>
            <p className="text-slate-600">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-slate-600 mt-2">
              <strong>Email:</strong> privacy@cloudsync.pro<br />
              <strong>Company:</strong> Sebastian Alliance Group
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200">
          <Link to="/" className="text-bronze hover:underline">← Back to Home</Link>
        </div>
      </main>
    </div>
  );
}
